import { ENDPOINT_EXAM_CONTEXT } from "@constants/urls";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import type { ExamDetails } from "@memorang/types/src/exams";
import type { Choice } from "@memorang/types/src/session/new-session";
import type { CurrentExamContext } from "../contexts/types";

const constructClientExamContext = ({
	exam: { name, bundles, icon, studyPackCollection, id },
	metadata: { examDate },
}: ExamDetails) => {
	const studyPack = bundles.find(
		(item) => item.bundle.bundleType === "STUDY_PACK",
	)?.bundle;
	const practiceTest = bundles.find(
		(item) => item.bundle.bundleType === "PRACTICE_TEST",
	)?.bundle;

	return {
		examName: name,
		examId: id,
		studyPackProductNumericId: studyPack?.numericId!,
		studyPackProductId: studyPack?.id!,
		practiceTestProductNumericId: practiceTest?.numericId,
		practiceTestProductId: practiceTest?.id!,
		examDate: examDate,
		examIcon: icon,
		studyPackCollectionId: studyPackCollection[0],
	};
};

const getNumPercenetageAnswered = (
	items: Choice[],
	currentChoiceNumFirstSelections: number,
	answered?: boolean,
) => {
	const isSomeItemWithMoreSelections = items.some(
		({ numFirstSelections }) => numFirstSelections && numFirstSelections > 10,
	);
	const showPercentAnswered = isSomeItemWithMoreSelections && answered;

	const totalfirstSelections = items.reduce(
		(a, b) => a + (b.numFirstSelections || 0),
		0,
	);

	const average = totalfirstSelections
		? (currentChoiceNumFirstSelections || 0) / totalfirstSelections
		: 0;

	const percentAnswered =
		totalfirstSelections && showPercentAnswered
			? Number.parseFloat((average * 100).toFixed(0))
			: undefined;
	return percentAnswered;
};

const storeExamContextInKV = async (
	examContext: CurrentExamContext,
	userId: string,
) => {
	try {
		await fetch(ENDPOINT_EXAM_CONTEXT, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				examContext,
				userId,
			}),
		});
	} catch (error) {
		const errorObj = new Error("Error storing exam context in KV");
		captureErrorToSentry(errorObj, {
			error: JSON.stringify(error),
		});
	}
};

const retrieveExamContextFromKV = async (userId: string) => {
	try {
		const response = await fetch(`${ENDPOINT_EXAM_CONTEXT}?userId=${userId}`);
		if (response.status === 200) {
			const data = await response.json();
			return data as CurrentExamContext;
		}
		return null;
	} catch (error) {
		console.error("Error:", error);
	}
};

export {
	constructClientExamContext,
	getNumPercenetageAnswered,
	storeExamContextInKV,
	retrieveExamContextFromKV,
};
